/**
 * Leitura do edital / termo de referência (D94), no navegador:
 * - sugere prazo de entrega, de pagamento e vigência da ata, sempre com o trecho de origem;
 * - calcula o último dia de impugnação e esclarecimento (Lei 14.133/2021, art. 164);
 * - confere a descrição de cada item do PNCP com o documento (⚠ quando diverge; "precisa analisar" quando não localiza).
 * Tudo é sugestão: o usuário aplica o que conferiu.
 */
import { useEffect, useMemo, useState } from "react";
import { useGetList, useNotify, useRefresh } from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import HelpIcon from "@mui/icons-material/HelpOutline";
import {
  conferirItem,
  extrairPrazosEdital,
  prazoImpugnacao,
  prepararDocumento,
  type ConferenciaItem,
  type SugestaoPrazo,
} from "@sobral/dominio";
import { useCelular } from "../componentes";
import { formatarData } from "../lib/formato";
import { textoDoPdf } from "../lib/pdf";
import { supabase } from "../lib/supabase";
import type { ItemLicitacao } from "./licitacao-itens";

export interface LicitacaoLeitura {
  id: string;
  data_hora_sessao: string | null;
  prazo_entrega_dias: number | null;
  prazo_pagamento_dias: number | null;
  vigencia_ata_meses: number | null;
  prazo_impugnacao: string | null;
  prazo_esclarecimento: string | null;
}

export interface DetalheConferencia extends ConferenciaItem {
  arquivo: string;
}

const NOME_CAMPO: Record<SugestaoPrazo["campo"], { rotulo: string; unidade: string }> = {
  prazo_entrega_dias: { rotulo: "Prazo de entrega", unidade: "dias" },
  prazo_pagamento_dias: { rotulo: "Prazo de pagamento", unidade: "dias" },
  vigencia_ata_meses: { rotulo: "Vigência da ata", unidade: "meses" },
};

/** Data da sessão no horário de Brasília ("aaaa-mm-dd"). */
const dataDaSessao = (iso: string) => new Date(iso).toLocaleDateString("sv-SE", { timeZone: "America/Maceio" });

export function DialogoLeitura({
  nome,
  pdf,
  licitacao,
  aoFechar,
}: {
  nome: string;
  /** Lê o PDF (do armazenamento ou de dentro do ZIP). */
  pdf: () => Promise<Blob>;
  licitacao: LicitacaoLeitura;
  aoFechar: () => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const celular = useCelular();
  const [texto, setTexto] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const [aplicados, setAplicados] = useState<Set<string>>(new Set());
  const { data: itens = [] } = useGetList<ItemLicitacao>("licitacao_item", {
    filter: { licitacao_id: licitacao.id },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "numero", order: "ASC" },
  });

  useEffect(() => {
    let ativo = true;
    void (async () => {
      try {
        const blob = await pdf();
        const t = await textoDoPdf(new File([blob], nome, { type: "application/pdf" }), 500);
        if (ativo) setTexto(t);
      } catch (e) {
        if (ativo) setErro((e as Error).message);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [nome]);

  const semTexto = texto !== null && texto.trim().length < 200;
  const prazos = useMemo(() => (texto && !semTexto ? extrairPrazosEdital(texto) : []), [texto, semTexto]);
  const documento = useMemo(() => (texto && !semTexto ? prepararDocumento(texto) : []), [texto, semTexto]);
  const conferencias = useMemo<{ item: ItemLicitacao; c: ConferenciaItem }[]>(
    () => (documento.length ? itens.map((item) => ({ item, c: conferirItem(item.descricao, documento) })) : []),
    [documento, itens],
  );
  const limite = licitacao.data_hora_sessao ? prazoImpugnacao(dataDaSessao(licitacao.data_hora_sessao)) : null;

  async function aplicar(chave: string, dados: Record<string, unknown>) {
    const { error } = await supabase.from("licitacao").update(dados).eq("id", licitacao.id);
    if (error) {
      notify(`Não foi possível aplicar: ${error.message}`, { type: "error" });
      return;
    }
    setAplicados((a) => new Set(a).add(chave));
    refresh();
  }

  async function gravarConferencia() {
    setGravando(true);
    const agora = new Date().toISOString();
    const falhas: string[] = [];
    for (const { item, c } of conferencias) {
      const detalhe: DetalheConferencia = { ...c, arquivo: nome };
      const { error } = await supabase
        .from("licitacao_item")
        .update({ conferencia_tr: c.situacao, conferencia_detalhe: detalhe, conferido_em: agora })
        .eq("id", item.id);
      if (error) falhas.push(`item ${item.numero}: ${error.message}`);
    }
    setGravando(false);
    notify(falhas.length ? `Conferência gravada em parte. Falharam: ${falhas.join("; ")}` : "Conferência dos itens gravada.", { type: falhas.length ? "warning" : "success" });
    refresh();
  }

  const contagem = (s: string) => conferencias.filter((x) => x.c.situacao === s).length;

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="lg" fullScreen={celular}>
      <DialogTitle>Leitura de {nome}</DialogTitle>
      <DialogContent>
        {erro && <Alert severity="error">Não foi possível ler o arquivo: {erro}</Alert>}
        {texto === null && !erro && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2">Lendo o PDF…</Typography>
          </Stack>
        )}
        {semTexto && (
          <Alert severity="warning">
            O PDF não tem texto legível (provavelmente escaneado). Os prazos e a conferência dos itens precisam ser feitos à mão.
          </Alert>
        )}
        {texto !== null && !semTexto && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Prazos
              </Typography>
              {prazos.length === 0 && <Typography variant="body2">Nenhum prazo de entrega, pagamento ou vigência encontrado no texto.</Typography>}
              <Table size="small">
                <TableBody>
                  {prazos.map((p) => {
                    const atual = licitacao[p.campo];
                    const igual = atual === p.valor;
                    return (
                      <TableRow key={p.campo}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{NOME_CAMPO[p.campo].rotulo}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <strong>
                            {p.valor} {NOME_CAMPO[p.campo].unidade}
                            {p.uteis ? " úteis" : ""}
                          </strong>
                          {p.outros.length > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              Também aparece: {p.outros.join(", ")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            “{p.trecho}”
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {igual || aplicados.has(p.campo) ? (
                            <Chip size="small" color="success" variant="outlined" label="Na licitação" />
                          ) : (
                            <Button size="small" variant="outlined" onClick={() => void aplicar(p.campo, { [p.campo]: p.valor })}>
                              {atual === null ? "Aplicar" : `Trocar ${atual} por ${p.valor}`}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>Impugnação e esclarecimento</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{limite ? <strong>até {formatarData(limite)}</strong> : "—"}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {limite
                          ? "3 dias úteis antes da abertura (Lei 14.133/2021, art. 164). Sábados e domingos descontados; feriados não: confira."
                          : "Informe a data da sessão na aba Dados para calcular."}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {limite &&
                        (licitacao.prazo_impugnacao === limite && licitacao.prazo_esclarecimento === limite ? (
                          <Chip size="small" color="success" variant="outlined" label="Na licitação" />
                        ) : aplicados.has("impugnacao") ? (
                          <Chip size="small" color="success" variant="outlined" label="Aplicado" />
                        ) : (
                          <Button size="small" variant="outlined" onClick={() => void aplicar("impugnacao", { prazo_impugnacao: limite, prazo_esclarecimento: limite })}>
                            Aplicar
                          </Button>
                        ))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {itens.length > 0 && (
              <Box>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="subtitle2">Itens do PNCP × documento</Typography>
                    <Chip size="small" color="success" variant="outlined" label={`${contagem("confere")} conferem`} />
                    <Chip size="small" color="warning" label={`${contagem("divergente")} divergem`} />
                    <Chip size="small" variant="outlined" label={`${contagem("nao_localizado")} a analisar`} />
                  </Stack>
                  <Button size="small" variant="contained" disabled={gravando} onClick={() => void gravarConferencia()}>
                    {gravando ? "Gravando…" : "Gravar conferência nos itens"}
                  </Button>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Situação</TableCell>
                      <TableCell>Diferença</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conferencias.map(({ item, c }) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                          {item.lote ? `${item.lote}.` : ""}
                          {item.numero}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <ChipSituacao situacao={c.situacao} />
                        </TableCell>
                        <TableCell>
                          <DetalheDiferenca detalhe={c} descricao={item.descricao} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ChipSituacao({ situacao }: { situacao: string }) {
  if (situacao === "confere") return <Chip size="small" color="success" variant="outlined" icon={<CheckIcon />} label="Confere" />;
  if (situacao === "divergente") return <Chip size="small" color="warning" icon={<WarningIcon />} label="Diverge" />;
  return <Chip size="small" variant="outlined" icon={<HelpIcon />} label="Precisa analisar" />;
}

function DetalheDiferenca({ detalhe, descricao }: { detalhe: ConferenciaItem; descricao: string }) {
  if (detalhe.situacao === "nao_localizado") {
    return (
      <Typography variant="caption" color="text.secondary">
        {detalhe.motivo === "descricao_curta"
          ? "A descrição do PNCP é curta demais para localizar o item no documento. Confira o termo de referência."
          : "O item não foi localizado no documento (pode estar em outro anexo ou em tabela de imagem)."}
      </Typography>
    );
  }
  return (
    <Box>
      {detalhe.situacao === "divergente" && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          No PNCP: <strong>{detalhe.faltando.join(", ")}</strong>
          {detalhe.extras.length > 0 && (
            <>
              {" "}
              · no documento: <strong>{detalhe.extras.join(", ")}</strong>
            </>
          )}
        </Typography>
      )}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <Box sx={{ flex: 1, p: 1, bgcolor: "background.default", borderRadius: 1 }}>
          <Typography variant="overline" color="text.secondary">
            PNCP
          </Typography>
          <Typography variant="caption" display="block">
            {descricao}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 1, bgcolor: "background.default", borderRadius: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Documento
          </Typography>
          <Typography variant="caption" display="block">
            {detalhe.trecho}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/** ⚠ no item quando a descrição do PNCP diverge do edital/TR (ou precisa ser analisada); clique mostra a diferença. */
export function ChipConferencia({ item }: { item: Pick<ItemLicitacao, "descricao" | "conferencia_tr" | "conferencia_detalhe" | "conferido_em"> }) {
  const [aberto, setAberto] = useState(false);
  if (!item.conferencia_tr || item.conferencia_tr === "confere") return null;
  const detalhe = item.conferencia_detalhe as DetalheConferencia | null;
  return (
    <>
      <Tooltip title={item.conferencia_tr === "divergente" ? "A descrição do PNCP diverge do edital/TR: clique para ver" : "Item não localizado no edital/TR: precisa analisar"}>
        <Chip
          size="small"
          color={item.conferencia_tr === "divergente" ? "warning" : "default"}
          variant={item.conferencia_tr === "divergente" ? "filled" : "outlined"}
          icon={item.conferencia_tr === "divergente" ? <WarningIcon /> : <HelpIcon />}
          label={item.conferencia_tr === "divergente" ? "Diverge do edital/TR" : "Precisa analisar"}
          onClick={() => setAberto(true)}
        />
      </Tooltip>
      {aberto && detalhe && (
        <Dialog open onClose={() => setAberto(false)} fullWidth maxWidth="md">
          <DialogTitle>Conferência com {detalhe.arquivo}</DialogTitle>
          <DialogContent>
            <DetalheDiferenca detalhe={detalhe} descricao={item.descricao} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAberto(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

/* ------------------------------------ Captação: arquivos e leitura automáticos (D99) ------------------------------------ */

export interface ResumoPreparacao {
  arquivos: number;
  falhas: string[];
  prazosPreenchidos: string[];
  itensConferidos: number;
  semTexto: boolean;
}

/** Textos dos PDFs da licitação (inclusive os de dentro de ZIP). */
async function textosDosArquivos(licitacaoId: string): Promise<{ nome: string; texto: string }[]> {
  const { data } = await supabase.from("licitacao_arquivo").select("arquivo_caminho, arquivo_nome").eq("licitacao_id", licitacaoId);
  const textos: { nome: string; texto: string }[] = [];
  for (const a of (data ?? []) as { arquivo_caminho: string; arquivo_nome: string | null }[]) {
    const { data: blob } = await supabase.storage.from("licitacoes").download(a.arquivo_caminho);
    if (!blob) continue;
    const nome = a.arquivo_nome ?? a.arquivo_caminho;
    if (/\.pdf$/i.test(a.arquivo_caminho)) {
      textos.push({ nome, texto: await textoDoPdf(new File([blob], nome, { type: "application/pdf" }), 500) });
    } else if (/\.zip$/i.test(a.arquivo_caminho)) {
      try {
        const { unzipSync } = await import("fflate");
        const conteudo = unzipSync(new Uint8Array(await blob.arrayBuffer()));
        for (const [interno, bytes] of Object.entries(conteudo)) {
          if (!/\.pdf$/i.test(interno) || interno.startsWith("__MACOSX/")) continue;
          const nomeInterno = interno.split("/").pop() ?? interno;
          textos.push({ nome: nomeInterno, texto: await textoDoPdf(new File([bytes.slice()], nomeInterno, { type: "application/pdf" }), 500) });
        }
      } catch {
        // ZIP corrompido ou protegido: fica para leitura manual.
      }
    }
  }
  return textos.filter((t) => t.texto.trim().length >= 200);
}

/**
 * Logo depois de captar (D99): baixa os arquivos publicados no PNCP, lê os PDFs e preenche o que estiver vazio na
 * licitação (prazos de entrega, pagamento, vigência da ata e o limite de impugnação/esclarecimento), e grava a
 * conferência dos itens. Nada que o usuário já preencheu é trocado.
 */
export async function prepararCaptura(licitacaoId: string, arquivosPncp: { url: string; titulo: string; tipo: string }[], aviso: (texto: string) => void): Promise<ResumoPreparacao> {
  const resumo: ResumoPreparacao = { arquivos: 0, falhas: [], prazosPreenchidos: [], itensConferidos: 0, semTexto: false };
  for (const [n, a] of arquivosPncp.entries()) {
    aviso(`Baixando ${n + 1} de ${arquivosPncp.length}…`);
    const { data, error } = await supabase.functions.invoke<{ ok?: boolean; erro?: string }>("pncp-arquivo", {
      body: { licitacao_id: licitacaoId, url: a.url, titulo: a.titulo, tipo: a.tipo },
    });
    if (error || !data?.ok) resumo.falhas.push(a.titulo);
    else resumo.arquivos += 1;
  }

  aviso("Lendo o edital…");
  const textos = await textosDosArquivos(licitacaoId);
  if (textos.length === 0) {
    resumo.semTexto = true;
    return resumo;
  }
  const tudo = textos.map((t) => t.texto).join("\n");
  const { data: lic } = await supabase
    .from("licitacao")
    .select("data_hora_sessao, prazo_entrega_dias, prazo_pagamento_dias, vigencia_ata_meses, prazo_impugnacao, prazo_esclarecimento")
    .eq("id", licitacaoId)
    .single();
  const atual = (lic ?? {}) as Partial<LicitacaoLeitura>;
  const preencher: Record<string, unknown> = {};
  for (const p of extrairPrazosEdital(tudo)) {
    if (atual[p.campo] === null || atual[p.campo] === undefined) {
      preencher[p.campo] = p.valor;
      resumo.prazosPreenchidos.push(`${NOME_CAMPO[p.campo].rotulo}: ${p.valor} ${NOME_CAMPO[p.campo].unidade}${p.uteis ? " úteis" : ""}`);
    }
  }
  if (atual.data_hora_sessao && !atual.prazo_impugnacao && !atual.prazo_esclarecimento) {
    const limite = prazoImpugnacao(dataDaSessao(atual.data_hora_sessao));
    preencher.prazo_impugnacao = limite;
    preencher.prazo_esclarecimento = limite;
    resumo.prazosPreenchidos.push(`Impugnação e esclarecimento até ${formatarData(limite)}`);
  }
  if (Object.keys(preencher).length) await supabase.from("licitacao").update(preencher).eq("id", licitacaoId);

  const { data: itens } = await supabase.from("licitacao_item").select("id, descricao").eq("licitacao_id", licitacaoId);
  const documento = prepararDocumento(tudo);
  const agora = new Date().toISOString();
  const nomes = textos.map((t) => t.nome).join(", ");
  for (const item of (itens ?? []) as { id: string; descricao: string }[]) {
    const c = conferirItem(item.descricao, documento);
    const detalhe: DetalheConferencia = { ...c, arquivo: nomes };
    const { error } = await supabase.from("licitacao_item").update({ conferencia_tr: c.situacao, conferencia_detalhe: detalhe, conferido_em: agora }).eq("id", item.id);
    if (!error) resumo.itensConferidos += 1;
  }
  return resumo;
}

/** Texto curto do que a captação fez, para o aviso na tela. */
export function textoDaPreparacao(r: ResumoPreparacao): string {
  const partes = [`${r.arquivos} arquivo(s) baixado(s)`];
  if (r.falhas.length) partes.push(`falharam: ${r.falhas.join(", ")}`);
  if (r.semTexto) partes.push("o edital não tem texto legível (leitura manual)");
  if (r.prazosPreenchidos.length) partes.push(`preenchido: ${r.prazosPreenchidos.join("; ")} (confira)`);
  if (r.itensConferidos) partes.push(`${r.itensConferidos} item(ns) conferido(s) com o edital`);
  return partes.join(" · ");
}
